import { Button, Input } from '@affine/component';
import { SettingsField } from '@affine/component/setting-components';
import { Upload } from '@affine/core/components/pure/file-upload';
import { uint8ArrayToBase64 } from '@affine/core/modules/workspace-engine/utils/base64';
import { useI18n } from '@affine/i18n';
import { useRef, useState } from 'react';

import type {
  ConnectionFileSetupField,
  ConnectionSetupField,
  ConnectionSetupPayload,
  ConnectionSetupValues,
} from './types';

export const EMPTY_SETUP_VALUES: ConnectionSetupValues = {
  fields: {},
  files: {},
};

const isFileField = (
  field: ConnectionSetupField
): field is ConnectionFileSetupField => field.type === 'file';

export function isSetupComplete(
  setupFields: readonly ConnectionSetupField[],
  values: ConnectionSetupValues
): boolean {
  return setupFields.every(field => {
    if (!field.required) return true;
    return isFileField(field)
      ? Boolean(values.files[field.name])
      : Boolean(values.fields[field.name]?.trim());
  });
}

/** The values as the server takes them; the file size stays in the browser. */
export function toSetupPayload(
  values: ConnectionSetupValues
): ConnectionSetupPayload {
  return {
    fields: { ...values.fields },
    files: Object.fromEntries(
      Object.entries(values.files).map(([name, file]) => [
        name,
        { fileName: file.fileName, contentBase64: file.contentBase64 },
      ])
    ),
  };
}

type FileProblem = 'too-large' | 'wrong-type';

function FileSetupField({
  field,
  fileName,
  disabled,
  onFile,
}: {
  field: ConnectionFileSetupField;
  fileName: string | undefined;
  disabled: boolean;
  onFile: (file: File | null) => void;
}) {
  const t = useI18n();
  const [problem, setProblem] = useState<FileProblem>();
  const types = field.accept.join(', ');
  const size = String(Math.floor(field.maxBytes / 1024));
  // Type and size are checked before the file is read.
  const pick = (file: File) => {
    const lowerName = file.name.toLowerCase();
    const next: FileProblem | undefined = !field.accept.some(extension =>
      lowerName.endsWith(extension)
    )
      ? 'wrong-type'
      : file.size > field.maxBytes
        ? 'too-large'
        : undefined;
    setProblem(next);
    onFile(next ? null : file);
  };
  return (
    <SettingsField
      label={field.label}
      hint={
        fileName ?? t['com.streamlain.connections.file.hint']({ types, size })
      }
      error={
        problem === 'too-large'
          ? t['com.streamlain.connections.file.too-large']({ size })
          : problem === 'wrong-type'
            ? t['com.streamlain.connections.file.wrong-type']({ types })
            : undefined
      }
    >
      {({ labelId, describedBy }) => (
        <Upload
          accept={field.accept.join(',')}
          fileChange={pick}
          disabled={disabled}
        >
          <Button
            variant="secondary"
            aria-labelledby={labelId}
            aria-describedby={describedBy}
            disabled={disabled}
            data-testid={`connection-setup-file-${field.name}`}
          >
            {fileName
              ? t['com.streamlain.connections.file.replace']()
              : t['com.streamlain.connections.file.choose']()}
          </Button>
        </Upload>
      )}
    </SettingsField>
  );
}

/** The setup fields of a service: text inputs and file choices. */
export function ConnectionSetupFields({
  setupFields,
  values,
  disabled,
  onChange,
}: {
  setupFields: readonly ConnectionSetupField[];
  values: ConnectionSetupValues;
  disabled: boolean;
  onChange: (values: ConnectionSetupValues) => void;
}) {
  // A file is read asynchronously; it lands in the values current by then.
  const latest = useRef(values);
  latest.current = values;
  const setFile = async (name: string, file: File | null) => {
    if (!file) {
      const { [name]: _dropped, ...files } = latest.current.files;
      onChange({ ...latest.current, files });
      return;
    }
    const contentBase64 = await uint8ArrayToBase64(
      new Uint8Array(await file.arrayBuffer())
    );
    onChange({
      ...latest.current,
      files: {
        ...latest.current.files,
        [name]: { fileName: file.name, contentBase64, byteSize: file.size },
      },
    });
  };
  return (
    <>
      {setupFields.map(field =>
        isFileField(field) ? (
          <FileSetupField
            key={field.name}
            field={field}
            fileName={values.files[field.name]?.fileName}
            disabled={disabled}
            onFile={file => void setFile(field.name, file)}
          />
        ) : (
          <SettingsField key={field.name} label={field.label}>
            {({ labelId }) => (
              <Input
                aria-labelledby={labelId}
                type={field.secret ? 'password' : 'text'}
                autoComplete="off"
                value={values.fields[field.name] ?? ''}
                required={field.required}
                disabled={disabled}
                onChange={value =>
                  onChange({
                    ...latest.current,
                    fields: { ...latest.current.fields, [field.name]: value },
                  })
                }
              />
            )}
          </SettingsField>
        )
      )}
    </>
  );
}
